import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb, Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { ServiceCategory } from '../types/Service';
import { serviceCatalogService } from '../services/serviceCatalogService';
import { CategorySelect } from '../../catalog/CategorySelect';
import { categoryPath, sortedCategories } from '../../catalog/categoryTree';
import { CategoryAttributeEditor } from '../../products/components/CategoryAttributeEditor';
import styles from '../../catalog/Catalog.module.css';

export const ServiceCategories: React.FC = () => {
  const { t } = useTranslation(['services', 'common']);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [items, setItems] = React.useState<ServiceCategory[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({ name: '', parentId: '', defaultMarkupPercent: '', sortOrder: '0' });
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const canCreate = hasPermission(PermissionScopes.servicesCreate);
  const canUpdate = hasPermission(PermissionScopes.servicesUpdate);
  const canDelete = hasPermission(PermissionScopes.servicesDelete);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setItems(await serviceCatalogService.getCategories());
      setError(null);
    } catch {
      setError(t('failedToLoadCategories', { ns: 'services' }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await serviceCatalogService.createCategory({
        name: form.name.trim(),
        parentId: form.parentId || undefined,
        defaultMarkupPercent: form.defaultMarkupPercent.trim() ? Number(form.defaultMarkupPercent) : undefined,
        sortOrder: form.sortOrder.trim() ? Number(form.sortOrder) : 0,
      });
      setForm({ name: '', parentId: '', defaultMarkupPercent: '', sortOrder: '0' });
      await load();
    } catch {
      setError(t('failedToSaveCategory', { ns: 'services' }));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: string) => {
    setSaving(true);
    try {
      await serviceCatalogService.deleteCategory(id);
      await load();
    } catch {
      setError(t('failedToDeleteCategory', { ns: 'services' }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner text={t('loading', { ns: 'services' })} /></div>;
  }

  return (
    <div className={styles.page}>
      <Breadcrumb items={[
        { label: t('title', { ns: 'services' }), path: '/services' },
        { label: t('categoryTitle', { ns: 'services' }), path: '/services/categories' },
      ]} />
      <div className={styles.headerRow}>
        <div>
          <h2 className={styles.title}>{t('categoryTitle', { ns: 'services' })}</h2>
        </div>
        <button className="btn btn-secondary" type="button" onClick={() => navigate('/services')}>{t('back', { ns: 'services' })}</button>
      </div>
      {error ? <div className="error">{error}</div> : null}

      {canCreate ? (
        <form className={styles.form} onSubmit={onSubmit}>
          <div className={styles.field}>
            <label className={styles.label}>{t('categoryName', { ns: 'services' })}</label>
            <input className={styles.input} value={form.name} placeholder={t('categoryNamePlaceholder', { ns: 'services' })} onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('parentCategory', { ns: 'services' })}</label>
            <CategorySelect
              className={styles.input}
              categories={items}
              value={form.parentId}
              onChange={parentId => setForm(prev => ({ ...prev, parentId }))}
              emptyLabel={t('noParent', { ns: 'services' })}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('defaultMarkup', { ns: 'services' })}</label>
            <input className={styles.input} type="number" min="0" step="0.01" value={form.defaultMarkupPercent} onChange={event => setForm(prev => ({ ...prev, defaultMarkupPercent: event.target.value }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('sortOrder', { ns: 'services' })}</label>
            <input className={styles.input} type="number" min="0" step="1" value={form.sortOrder} onChange={event => setForm(prev => ({ ...prev, sortOrder: event.target.value }))} />
          </div>
          <div className={styles.actions}>
            <button type="submit" className="btn btn-primary" disabled={saving}>{t('addCategory', { ns: 'services' })}</button>
          </div>
        </form>
      ) : null}

      {items.length === 0 ? (
        <div className={styles.emptyState}>{t('emptyCategories', { ns: 'services' })}</div>
      ) : (
        sortedCategories(items).map(item => (
          <div key={item.id} className={styles.categoryBlock}>
            <div className={styles.categoryRow}>
              <div>
                <div className={styles.primaryText}>{categoryPath(items, item.id)}</div>
                <div className={styles.secondaryText}>
                  {item.parentId ? categoryPath(items, item.parentId) : t('noParent', { ns: 'services' })}
                </div>
              </div>
              <div className={styles.categoryActions}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setExpandedId(current => current === item.id ? null : item.id)}
                >
                  {expandedId === item.id ? t('hideFields', { ns: 'services' }) : t('showFields', { ns: 'services' })}
                </button>
                {canDelete ? (
                  <button type="button" className="btn btn-secondary" onClick={() => onDelete(item.id)} disabled={saving}>
                    {t('deleteCategory', { ns: 'services' })}
                  </button>
                ) : null}
              </div>
            </div>
            {expandedId === item.id ? (
              <CategoryAttributeEditor categoryId={item.id} canUpdate={canUpdate} ns="services" api={serviceCatalogService} />
            ) : null}
          </div>
        ))
      )}
    </div>
  );
};

export default ServiceCategories;
